import React, { useMemo, useRef, useState } from 'react';

import { Input, Select } from 'antd';
import { ADD_RECORD_SAVE_FAILED } from 'constants/messageConstants';
import {
  ADD_RECORD_ADDING_LABEL,
  ADD_RECORD_BUTTON_LABEL,
  ADD_VARIETY_LABELS,
} from 'constants/uiConstants';
import { useFetchGTechStacks } from 'hooks/useFetchGTechStacks';
import { v4 as uuidv4 } from 'uuid';
import MergeClassNames from 'utils/MergeClassNames';

import type { GcVariety } from 'interfaces/IGlobalCatalogue';

import { isNewVarietyValid, NewVarietyValues } from './addVariety';
import { FLYOUT_ADD_BUTTON_ATTRIBUTE, StepSelectorOption } from './stepConfig';
import { useCreateMVariety } from './useCreateMVariety';

interface AddVarietyFormProps {
  /** The crop the variety is linked to. */
  crop: StepSelectorOption;
  initialName: string;
  /** Called with the crop and new variety as one selection, and the variety saved. */
  onCreated: (selection: StepSelectorOption, variety: GcVariety) => void;
}

/**
 * Add form for a missing variety (OP-2498), in a flyout beside the step picker
 * or inline under a plan's variety field. The variety is linked to the crop it
 * was opened from.
 */
const AddVarietyForm: React.FC<AddVarietyFormProps> = ({
  crop,
  initialName,
  onCreated,
}) => {
  // One id of each per open form, reused on retry so a resend can't create two.
  const varietyIdRef = useRef(uuidv4());
  const cropDocIdRef = useRef(uuidv4());
  const { mutateAsync, isPending, isError } = useCreateMVariety();
  const { data: techStacks = [] } = useFetchGTechStacks();
  const techStackOptions = useMemo(
    () => techStacks.map((stack) => ({ value: stack.id, label: stack.name })),
    [techStacks]
  );

  const [values, setValues] = useState<NewVarietyValues>({
    name: initialName.trim(),
  });
  const canSave = isNewVarietyValid(values) && !isPending;

  const handleSave = () => {
    if (!canSave) return;
    // Not mutate()'s onSuccess: see AddRecordForm.
    mutateAsync({
      varietyId: varietyIdRef.current,
      cropDocId: cropDocIdRef.current,
      crop,
      values,
    }).then(
      (selection) =>
        onCreated(selection, {
          varietyId: selection.globalId2 ?? varietyIdRef.current,
          varietyName: selection.item2 ?? values.name.trim(),
          techStackId: selection.techStackId ?? null,
          snapTechStack: selection.snapTechStack ?? null,
          supplierId: selection.supplierId ?? null,
          snapSupplier: selection.snapSupplier ?? null,
        }),
      () => undefined // the failure shows inline via isError
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-3 overflow-y-auto p-3">
        <div className="col-span-2 flex flex-col gap-1">
          <label
            htmlFor="add-variety-name"
            className="text-sm font-semibold text-gray-dark"
          >
            {ADD_VARIETY_LABELS.name}
          </label>
          <Input
            id="add-variety-name"
            autoFocus
            value={values.name}
            onChange={(event) =>
              setValues((current) => ({ ...current, name: event.target.value }))
            }
            onPressEnter={handleSave}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <label
            htmlFor="add-variety-supplier"
            className="text-sm font-semibold text-gray-dark"
          >
            {ADD_VARIETY_LABELS.supplier}
          </label>
          <Input
            id="add-variety-supplier"
            value={values.supplier ?? ''}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                supplier: event.target.value,
              }))
            }
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <label
            htmlFor="add-variety-tech-stack"
            className="text-sm font-semibold text-gray-dark"
          >
            {ADD_VARIETY_LABELS.techStack}
          </label>
          <Select
            id="add-variety-tech-stack"
            className="w-full"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder={ADD_VARIETY_LABELS.selectTechStack}
            options={techStackOptions}
            value={values.techStack?.id}
            getPopupContainer={() => document.body}
            onChange={(id?: string) => {
              const stack = techStacks.find((s) => s.id === id);
              setValues((current) => ({
                ...current,
                techStack: stack ? { id: stack.id, name: stack.name } : null,
              }));
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 p-3">
        {isError && (
          <p role="alert" className="text-xs font-semibold text-danger-dark">
            {ADD_RECORD_SAVE_FAILED}
          </p>
        )}
        <button
          type="button"
          {...{ [FLYOUT_ADD_BUTTON_ATTRIBUTE]: '' }}
          disabled={!canSave}
          className={MergeClassNames(
            'w-full rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-medium text-white',
            !canSave && 'cursor-not-allowed opacity-50'
          )}
          onClick={handleSave}
        >
          {isPending ? ADD_RECORD_ADDING_LABEL : ADD_RECORD_BUTTON_LABEL}
        </button>
      </div>
    </div>
  );
};

export default AddVarietyForm;
